import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { site } from "../../content/site";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#711457]/85 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 h-20 flex items-center justify-between">
        <div className="flex items-center">
          <Link
            to="/"
            data-testid="brand-link"
            className="flex items-center gap-3 group"
          >
            <img
              src={site.brand.logo}
              alt={`${site.brand.name} logo`}
              className="h-10 w-10 sm:h-11 sm:w-11 object-cover rounded-full ring-1 ring-white/25 group-hover:ring-white/60 transition-all"
            />
            <span className="font-serif-display text-xl sm:text-2xl text-white tracking-tight whitespace-nowrap">
              HawkEdge<span className="hidden md:inline"> AI Advisory</span>
            </span>
          </Link>
        </div>

        <nav
          className="hidden lg:flex items-center gap-10"
          data-testid="primary-nav"
        >
          {site.nav.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              end={item.href === "/"}
              data-testid={`nav-link-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[13px] font-sans-modern font-medium tracking-wide transition-colors ${
                  isActive ? "text-white" : "text-white/60 hover:text-white"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Link
            to="/contact"
            data-testid="header-cta"
            className="inline-flex items-center justify-center h-10 px-6 bg-white text-[#711457] font-sans-modern font-semibold text-[13px] tracking-wide hover:bg-slate-200 transition-colors"
          >
            Request consultation
          </Link>
        </div>

        <button
          type="button"
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
          className="lg:hidden inline-flex items-center justify-center w-10 h-10 text-white/80 hover:text-white"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          className="lg:hidden border-t border-white/10 bg-[#711457]/95 backdrop-blur-xl"
          data-testid="mobile-menu"
        >
          <nav className="px-6 py-6 flex flex-col gap-1">
            {site.nav.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                end={item.href === "/"}
                data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `py-3 border-b border-white/5 font-serif-display text-2xl ${
                    isActive ? "text-white" : "text-white/70"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-cta"
              className="mt-6 inline-flex items-center justify-center h-12 bg-white text-[#711457] font-sans-modern font-semibold text-sm tracking-wide"
            >
              Request consultation
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
